import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SimpleButton from "../SimpleButton";
import CsvDropzone from "./CsvDropzone";
import CsvPreviewTable from "./CsvPreviewTable";
import { extractApiError } from "../../../utils/validation";
import {
  CONTENT_COLUMNS,
  CsvRow,
  MAX_CSV_BYTES,
  MAX_CSV_ROWS,
  ParsedContentCsv,
  parseContentCsv,
} from "./parseContentCsv";
import {
  CONTENT_TYPES,
  RowIssue,
  findDuplicateIndexes,
  isRewrittenDate,
  normalisePublishDate,
  toIngestionPayload,
  validateContentRow,
} from "./validateContentRow";
import { buildErrorReportCsv, downloadCsv, TEMPLATE_CSV } from "./csvFile";

type Stage = "idle" | "parsing" | "preview" | "uploading" | "done";

interface RowOutcome {
  row: CsvRow;
  success: boolean;
  message: string;
  contentId?: string;
}

interface ManualCsvUploadProps {
  onAlert: (alert: {
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }) => void;
}

/** Every issue on a row, flattened into one sentence for the error report. */
function describeIssues(issues: RowIssue[]): string {
  return issues.map((issue) => `${issue.field}: ${issue.message}`).join(" ");
}

export default function ManualCsvUpload({ onAlert }: ManualCsvUploadProps) {
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedContentCsv | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [issuesByIndex, setIssuesByIndex] = useState<Record<number, RowIssue[]>>({});
  const [duplicateIndexes, setDuplicateIndexes] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [outcomes, setOutcomes] = useState<RowOutcome[]>([]);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const rows = parsed?.rows ?? [];
  const validCount = rows.filter((row) => (issuesByIndex[row.index] ?? []).length === 0).length;
  const invalidCount = rows.length - validCount;
  const selectedRows = rows.filter((row) => selected.has(row.index));
  // Dates that will be sent in a different form than they were written in. Worth
  // saying out loud: 03/04/2026 is read as 4 March, and a European spreadsheet meant
  // 3 April.
  const rewrittenDates = rows.filter((row) => isRewrittenDate(row.values.publish_date));

  const resetToIdle = () => {
    setStage("idle");
    setFileName(null);
    setParsed(null);
    setFileError(null);
    setIssuesByIndex({});
    setDuplicateIndexes(new Set());
    setSelected(new Set());
    setOutcomes([]);
  };

  const handleFile = async (file: File) => {
    setFileError(null);
    setFileName(file.name);

    if (file.size > MAX_CSV_BYTES) {
      setParsed(null);
      setStage("idle");
      setFileError(
        `That file is ${(file.size / (1024 * 1024)).toFixed(1)} MB; the maximum is ${MAX_CSV_BYTES / (1024 * 1024)} MB.`,
      );
      return;
    }

    setStage("parsing");
    try {
      const result = parseContentCsv(await file.text());

      if (result.error) {
        setParsed(null);
        setStage("idle");
        setFileError(result.error);
        return;
      }

      const issues: Record<number, RowIssue[]> = {};
      result.rows.forEach((row) => {
        issues[row.index] = validateContentRow(row.values);
      });

      setParsed(result);
      setIssuesByIndex(issues);
      setDuplicateIndexes(findDuplicateIndexes(result.rows));
      // Valid rows start selected; invalid ones cannot be selected at all.
      setSelected(
        new Set(
          result.rows.filter((row) => issues[row.index].length === 0).map((row) => row.index),
        ),
      );
      setStage("preview");
    } catch (error) {
      console.error("Error reading CSV file:", error);
      setParsed(null);
      setStage("idle");
      setFileError("That file could not be read. Make sure it is a UTF-8 encoded CSV.");
    }
  };

  const toggleRow = (index: number) => {
    const next = new Set(Array.from(selected));
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setSelected(next);
  };

  const toggleAll = (select: boolean) => {
    if (!select) {
      setSelected(new Set());
      return;
    }
    setSelected(
      new Set(
        rows.filter((row) => (issuesByIndex[row.index] ?? []).length === 0).map((row) => row.index),
      ),
    );
  };

  const handleSubmit = async () => {
    if (selectedRows.length === 0) return;

    const controller = new AbortController();
    setAbortController(controller);
    setStage("uploading");

    const token = localStorage.getItem("access");
    // One request for the whole selection: the file is capped at MAX_CSV_ROWS and the
    // endpoint accepts that many per call, so a valid upload always fits in one POST.
    try {
      const response = await fetch("/api/pipeline/ingestion/manual/bulk/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          items: selectedRows.map((row) => toIngestionPayload(row.values)),
        }),
        signal: controller.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        // The envelope itself was refused (auth, size, shape) — nothing was ingested.
        const reason = extractApiError(data, `Upload failed with status ${response.status}.`);
        setOutcomes(selectedRows.map((row) => ({ row, success: false, message: reason })));
        setStage("done");
        setAbortController(null);
        onAlert({ title: "Upload Failed", message: reason, type: "error" });
        return;
      }

      const results: any[] = Array.isArray(data?.results) ? data.results : [];
      const collected: RowOutcome[] = selectedRows.map((row, position) => {
        const result = results.find((entry) => entry?.index === position);
        if (result?.success) {
          return {
            row,
            success: true,
            message: result.message ?? "Ingested",
            contentId: result.content_id,
          };
        }
        return {
          row,
          success: false,
          message: extractApiError(result, "The server rejected this row without a reason."),
        };
      });

      setOutcomes(collected);
      setStage("done");
      setAbortController(null);

      const succeeded = collected.filter((outcome) => outcome.success).length;
      onAlert({
        title: succeeded === collected.length ? "Content Ingested" : "Partly Ingested",
        message:
          succeeded === collected.length
            ? `${succeeded} item${succeeded === 1 ? "" : "s"} ingested and queued for processing.`
            : `${succeeded} of ${collected.length} rows ingested. The rest are listed below.`,
        type: succeeded === collected.length ? "success" : "warning",
      });
    } catch (error: any) {
      setStage("done");
      setAbortController(null);

      if (error.name === "AbortError") {
        // The request may still have been processed server-side, so say nothing about
        // what was or was not ingested — the dashboard is the source of truth.
        setOutcomes([]);
        onAlert({
          title: "Upload Cancelled",
          message: "The upload was stopped. Check the dashboard for anything that got through.",
          type: "warning",
        });
        return;
      }

      console.error("Error uploading CSV rows:", error);
      setOutcomes(
        selectedRows.map((row) => ({
          row,
          success: false,
          message: "Network error: the server could not be reached.",
        })),
      );
      onAlert({
        title: "Network Error",
        message: "Unable to connect to the server. No rows were ingested.",
        type: "error",
      });
    }
  };

  const failedOutcomes = outcomes.filter((outcome) => !outcome.success);
  const succeededCount = outcomes.length - failedOutcomes.length;

  const downloadErrorReport = () => {
    // Rows rejected before submission are as much a part of "what still needs
    // fixing" as rows the server refused, so both go into one report.
    const skipped = rows
      .filter((row) => (issuesByIndex[row.index] ?? []).length > 0)
      .map((row) => ({ row, reason: describeIssues(issuesByIndex[row.index]) }));
    const rejected = failedOutcomes.map((outcome) => ({
      row: outcome.row,
      reason: outcome.message,
    }));
    downloadCsv(
      `ingestion-errors-${fileName ?? "upload"}`,
      buildErrorReportCsv([...rejected, ...skipped]),
    );
  };

  const stepNumber = stage === "done" ? 3 : stage === "preview" || stage === "uploading" ? 2 : 1;

  return (
    <div className="space-y-5">
      {/* Step indicator */}
      <ol className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
        {["Upload file", "Review rows", "Result"].map((label, position) => {
          const step = position + 1;
          const active = step === stepNumber;
          const complete = step < stepNumber;
          return (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full font-bold ${
                  active
                    ? "bg-orange text-white"
                    : complete
                      ? "bg-mint-green text-dark-grey"
                      : "bg-gray-200 text-gray-500"
                }`}
              >
                {complete ? "✓" : step}
              </span>
              <span className={active ? "text-dark-grey font-semibold" : "text-gray-500"}>
                {label}
              </span>
              {step < 3 && <span className="text-gray-300 px-1">→</span>}
            </li>
          );
        })}
      </ol>

      {/* Step 1 — pick a file */}
      {(stage === "idle" || stage === "parsing") && (
        <>
          <CsvDropzone
            onFile={handleFile}
            fileName={fileName}
            disabled={stage === "parsing"}
          />

          {fileError && (
            <p className="rounded-xl bg-red-50 border-2 border-red-200 px-4 py-3 text-sm text-red-700">
              {fileError}
            </p>
          )}

          <div className="rounded-xl bg-off-white px-4 py-3 text-xs sm:text-sm text-dark-grey">
            <p className="font-semibold mb-1">Expected columns</p>
            <p className="font-mono break-all mb-2">{CONTENT_COLUMNS.join(",")}</p>
            <ul className="list-disc pl-5 space-y-0.5 text-gray-600">
              <li>
                <span className="font-semibold">content_type</span>, title, author and text are
                required; source_url and publish_date are optional.
              </li>
              <li>content_type must be one of: {CONTENT_TYPES.join(", ")}.</li>
              <li>text needs at least 50 characters; wrap it in double quotes if it contains commas or line breaks.</li>
              <li>
                publish_date accepts YYYY-MM-DD or MM/DD/YYYY (four-digit year). Column order does
                not matter; extra columns are ignored.
              </li>
              <li>Up to {MAX_CSV_ROWS} rows per file.</li>
            </ul>
            <button
              type="button"
              onClick={() => downloadCsv("ingestion-template.csv", TEMPLATE_CSV)}
              className="mt-2 font-semibold text-orange hover:underline"
            >
              ⤓ Download template CSV
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-1">
            {stage === "parsing" && (
              <span className="text-sm text-gray-500">Reading file…</span>
            )}
            <SimpleButton
              text="Cancel"
              colour="yellow"
              onClick={() => navigate("/pipeline/dashboard")}
            />
          </div>
        </>
      )}

      {/* Step 2 — review and submit */}
      {(stage === "preview" || stage === "uploading") && parsed && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-dark-grey font-semibold break-all">{fileName}</span>
            <span className="text-gray-600">{rows.length} rows</span>
            <span className="text-green-700 font-semibold">{validCount} valid</span>
            {invalidCount > 0 && (
              <span className="text-red-600 font-semibold">{invalidCount} with problems</span>
            )}
            {duplicateIndexes.size > 0 && (
              <span className="text-dark-grey font-semibold">
                {duplicateIndexes.size} duplicate title + author
              </span>
            )}
            <span className="text-gray-600">{selectedRows.length} selected</span>
          </div>

          {parsed.ignoredHeaders.length > 0 && (
            <p className="text-xs sm:text-sm text-gray-500">
              Ignored unrecognised column{parsed.ignoredHeaders.length > 1 ? "s" : ""}:{" "}
              {parsed.ignoredHeaders.join(", ")}
            </p>
          )}

          {rewrittenDates.length > 0 && (
            <p className="text-xs sm:text-sm text-gray-500">
              Read {rewrittenDates.length} publish date
              {rewrittenDates.length > 1 ? "s" : ""} as month/day/year — for example{" "}
              <span className="font-mono">{rewrittenDates[0].values.publish_date.trim()}</span> is
              stored as{" "}
              <span className="font-mono">
                {normalisePublishDate(rewrittenDates[0].values.publish_date)}
              </span>
              . Write dates as YYYY-MM-DD if that is not what you meant.
            </p>
          )}

          {invalidCount > 0 && (
            <p className="rounded-xl bg-red-50 border-2 border-red-200 px-4 py-3 text-sm text-red-700">
              {invalidCount} row{invalidCount > 1 ? "s" : ""} cannot be submitted. Fix{" "}
              {invalidCount > 1 ? "them" : "it"} in the file and upload again, or continue with the
              valid rows only.
            </p>
          )}

          {duplicateIndexes.size > 0 && (
            <p className="rounded-xl bg-yellow/20 border-2 border-yellow px-4 py-3 text-sm text-dark-grey">
              Some rows repeat an earlier row's title and author. Ingestion does not deduplicate, so
              submitting them creates separate content items. Uncheck any you did not mean to
              include.
            </p>
          )}

          <CsvPreviewTable
            rows={rows}
            issuesByIndex={issuesByIndex}
            duplicateIndexes={duplicateIndexes}
            selected={selected}
            onToggle={toggleRow}
            onToggleAll={toggleAll}
            disabled={stage === "uploading"}
          />

          {stage === "uploading" && (
            <p className="text-sm text-dark-grey" role="status">
              <span className="font-semibold">
                Ingesting {selectedRows.length} row{selectedRows.length === 1 ? "" : "s"}…
              </span>{" "}
              <span className="text-gray-500">Keep this tab open until the upload finishes.</span>
            </p>
          )}

          <div className="flex flex-wrap gap-2 sm:gap-3 pt-1">
            {stage === "uploading" ? (
              <SimpleButton
                text="Stop upload"
                colour="red"
                onClick={() => abortController?.abort()}
              />
            ) : (
              <>
                <SimpleButton
                  text={
                    selectedRows.length === 0
                      ? "No rows selected"
                      : `Ingest ${selectedRows.length} row${selectedRows.length === 1 ? "" : "s"}`
                  }
                  colour="orange"
                  onClick={handleSubmit}
                  disabled={selectedRows.length === 0}
                />
                <SimpleButton text="Choose another file" colour="yellow" onClick={resetToIdle} />
              </>
            )}
          </div>
        </>
      )}

      {/* Step 3 — outcome */}
      {stage === "done" && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-green-700 font-semibold">{succeededCount} ingested</span>
            {failedOutcomes.length > 0 && (
              <span className="text-red-600 font-semibold">{failedOutcomes.length} failed</span>
            )}
            {invalidCount > 0 && (
              <span className="text-gray-600">{invalidCount} skipped as invalid</span>
            )}
          </div>

          {succeededCount > 0 && (
            <p className="rounded-xl bg-green-50 border-2 border-green-200 px-4 py-3 text-sm text-green-800">
              {succeededCount} item{succeededCount === 1 ? " is" : "s are"} queued for tagging and
              filtering. Processing runs a couple of items at a time, so a large upload finishes
              gradually — follow it on the dashboard.
            </p>
          )}

          {failedOutcomes.length > 0 && (
            <div className="overflow-x-auto rounded-xl border-2 border-gray-200">
              <div className="max-h-72 overflow-y-auto">
                <table className="min-w-full text-xs sm:text-sm">
                  <thead className="sticky top-0 bg-off-white">
                    <tr className="text-left text-dark-grey">
                      <th scope="col" className="px-3 py-2 w-12 font-semibold">#</th>
                      <th scope="col" className="px-3 py-2 font-semibold">Title</th>
                      <th scope="col" className="px-3 py-2 font-semibold">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {failedOutcomes.map((outcome) => (
                      <tr key={outcome.row.index} className="bg-red-50">
                        <td className="px-3 py-2 align-top text-gray-500">{outcome.row.index}</td>
                        <td className="px-3 py-2 align-top max-w-[16rem]">
                          <span className="block truncate" title={outcome.row.values.title}>
                            {outcome.row.values.title.trim() || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top text-red-700">{outcome.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 sm:gap-3 pt-1">
            <SimpleButton
              text="Go to dashboard"
              colour="orange"
              onClick={() => navigate("/pipeline/dashboard")}
            />
            <SimpleButton text="Upload another file" colour="yellow" onClick={resetToIdle} />
            {(failedOutcomes.length > 0 || invalidCount > 0) && (
              <button
                type="button"
                onClick={downloadErrorReport}
                className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-full border-2 border-gray-300 font-bold text-dark-grey text-sm sm:text-base transition hover:bg-gray-50"
              >
                ⤓ Download error report
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
